import { ReactNode } from "react";
import { ArrowRight, Phone } from "lucide-react";
import { GenieAvatar } from "@/components/GenieAvatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { getSeededGenieDefaultImage } from "@/data/genie/defaults";
import { DemoCardProps } from '@/types/ui/public-page';

interface VoiceVisualizationProps {
  colorFrom: string;
  colorTo: string;
}

const VoiceVisualization = ({ colorFrom, colorTo }: VoiceVisualizationProps) => (
  <div className="flex justify-center gap-1">
    {[...Array(7)].map((_, i) => (
      <div
        key={i}
        className={`w-2 bg-gradient-to-t from-${colorFrom} to-${colorTo} rounded-full transition-all duration-300 animate-pulse`}
        style={{
          height: `${Math.random() * 30 + 20}px`,
          animationDelay: `${i * 0.1}s`,
        }}
      />
    ))}
  </div>
);


const DemoCard = ({
  genieId,
  firstMessage,
  onDemoClick,
  demoButtonText,
  topBadge,
  bottomBadge,
  gradientColors = { from: "genie-primary", to: "genie-logo" }
}: DemoCardProps) => (
  <div className="relative w-full max-w-md mt-8 lg:mt-0">
    <div className="bg-white rounded-2xl p-8 shadow-2xl border border-gray-200">
      <div className="flex flex-col items-center">
        <GenieAvatar
          speaking={true}
          listening={false}
          color="#9333ea"
          secondaryColor="#2563eb"
          gradientEnabled={true}
          customAvatarUrl={getSeededGenieDefaultImage(genieId)}
        />

        <div className="mt-6 space-y-4">
          <VoiceVisualization
            colorFrom={gradientColors.from}
            colorTo={gradientColors.to}
          />

          <p className="text-gray-700 font-medium text-center">
            "{firstMessage}"
          </p>

          <Button
            onClick={onDemoClick}
            className={`w-full bg-gradient-to-r from-${gradientColors.from} to-${gradientColors.to} hover:from-${gradientColors.from}/90 hover:to-${gradientColors.to}/90 text-white`}
          >
            <Phone className="mr-2 h-4 w-4" />
            {demoButtonText}
          </Button>
        </div>
      </div>
    </div>

    {/* Floating badges */}
    {topBadge && (
      <div className="absolute -top-4 -right-4 bg-[#D6409F] text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
        {topBadge}
      </div>
    )}
    {bottomBadge && (
      <div className="absolute -bottom-4 -left-4 bg-genie-primary text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
        {bottomBadge}
      </div>
    )}
  </div>
);

interface PublicHeroSectionProps {
  backgroundImage: string;
  gradientOverlay: string;
  badge?: ReactNode;
  title: string;
  tagline: string;
  description: string;
  primaryButton: {
    text: string;
    onClick: () => void;
    disabled?: boolean;
  };
  secondaryButton: {
    text: string;
    onClick: () => void;
  };
  quickStats?: ReactNode;
  demo: DemoCardProps;
}

const PublicHeroSection = ({
  backgroundImage,
  gradientOverlay,
  badge,
  title,
  tagline,
  description,
  primaryButton,
  secondaryButton,
  quickStats,
  demo
}: PublicHeroSectionProps) => {
  return (
    <section className="relative w-full min-h-[800px] flex items-center justify-center text-white overflow-hidden">
      {/* Background with gradient overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src={backgroundImage}
          alt={`${title} background`}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.currentTarget.src = "/hero-background.webp";
          }}
        />
        <div className={`absolute inset-0 ${gradientOverlay} z-10`}></div>
      </div>

      {/* Content */}
      <div className="relative z-20 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left: Text Content */}
          <div className="text-center lg:text-left">
            {badge && <div className="mb-4">{badge}</div>}

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-8 leading-tight">
              {title}
            </h1>

            <p className="text-xl lg:text-2xl text-white/90 mb-8 font-medium">
              {tagline}
            </p>

            <p className="text-lg text-white/80 mb-10">
              {description}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Button
                onClick={primaryButton.onClick}
                disabled={primaryButton.disabled}
                size="lg"
                className="bg-white text-gray-900 hover:bg-gray-100 font-semibold shadow-2xl border-2 border-white/30 px-8 py-6 text-lg"
              >
                {primaryButton.text}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>

              <Button
                variant="outline"
                size="lg"
                className="bg-white/10 text-white border-2 border-white hover:bg-white/20 font-semibold shadow-xl px-8 py-6 text-lg"
                onClick={secondaryButton.onClick}
              >
                <Phone className="mr-2 h-5 w-5" />
                {secondaryButton.text}
              </Button>
            </div>

            {/* Quick Stats */}
            {quickStats && (
              <div className="mt-8">
                {quickStats}
              </div>
            )}
          </div>

          {/* Right: Interactive Demo */}
          <div className="flex justify-center items-center">
            <DemoCard {...demo} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default PublicHeroSection;